import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, X, Search } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ProductCard from '../components/ProductCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Shop = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  
  const selectedCategory = searchParams.get('category') || '';
  const showFeatured = searchParams.get('featured') === 'true';
  const searchQuery = searchParams.get('search') || '';

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [selectedCategory, showFeatured, searchQuery]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      let url = `${API}/products`;
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (showFeatured) params.append('featured', 'true');
      if (searchQuery) params.append('search', searchQuery);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setProducts(response.data);
    } catch (error) {
      setProducts([]);
      toast.error('No se pudieron cargar los productos');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/products/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      setCategories([]);
      toast.error('No se pudieron cargar las categorías');
    }
  };

  const handleCategoryChange = (category) => {
    if (category) {
      setSearchParams({ category });
    } else {
      setSearchParams({});
    }
    setShowFilters(false);
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  return (
    <div className="pt-20 min-h-screen bg-[#FAFAF9]" data-testid="shop-page">
      {/* Header */}
      <div className="bg-white border-b border-[#E7E5E4]">
        <div className="container-luxury py-16 md:py-24">
          <div className="text-center">
            <p className="text-sm tracking-[0.2em] uppercase text-[#A8A29E] mb-4">
              {searchQuery ? 'Resultados de búsqueda' : 'Colección'}
            </p>
            <h1 
              className="text-4xl md:text-6xl font-light tracking-tight text-[#1C1917]"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {searchQuery ? `"${searchQuery}"` : selectedCategory || 'Todos los Productos'}
            </h1>
            {(selectedCategory || showFeatured || searchQuery) && (
              <button
                onClick={clearFilters}
                className="mt-6 text-sm text-[#BA7B7C] hover:text-[#A36566] transition-colors inline-flex items-center space-x-2"
                data-testid="clear-filters-btn"
              >
                <X className="w-4 h-4" />
                <span>Limpiar filtros</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="container-luxury py-12 md:py-16">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Desktop Sidebar Filters */}
          <aside className="hidden lg:block w-64 flex-shrink-0" data-testid="desktop-filters">
            <div className="sticky top-28">
              <h3 className="text-sm tracking-[0.2em] uppercase text-[#A8A29E] mb-6">
                Categorías
              </h3>
              <ul className="space-y-3">
                <li>
                  <button
                    onClick={() => handleCategoryChange('')}
                    className={`text-sm transition-colors ${
                      !selectedCategory 
                        ? 'text-[#BA7B7C] font-medium' 
                        : 'text-[#57534E] hover:text-[#BA7B7C]'
                    }`}
                    data-testid="filter-all"
                  >
                    Todos los productos
                  </button>
                </li>
                {categories.map((category) => (
                  <li key={category}>
                    <button
                      onClick={() => handleCategoryChange(category)}
                      className={`text-sm transition-colors ${
                        selectedCategory === category 
                          ? 'text-[#BA7B7C] font-medium' 
                          : 'text-[#57534E] hover:text-[#BA7B7C]'
                      }`}
                      data-testid={`filter-${category}`}
                    >
                      {category}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Mobile Filter Button */}
            <div className="lg:hidden mb-6 flex items-center justify-between">
              <p className="text-sm text-[#57534E]">
                {products.length} productos
              </p>
              <button
                onClick={() => setShowFilters(true)}
                className="flex items-center space-x-2 text-sm text-[#57534E] hover:text-[#BA7B7C] transition-colors"
                data-testid="mobile-filter-btn"
              >
                <SlidersHorizontal className="w-4 h-4" />
                <span>Filtrar</span>
              </button>
            </div>

            {/* Products Grid */}
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
                {[...Array(6)].map((_, i) => (
                  <div key={`skeleton-shop-${i}`} className="animate-pulse">
                    <div className="bg-[#F5F0EB] aspect-[3/4] rounded-2xl mb-4"></div>
                    <div className="h-3 bg-[#F5F0EB] rounded w-1/3 mb-2"></div>
                    <div className="h-5 bg-[#F5F0EB] rounded w-2/3 mb-2"></div>
                    <div className="h-4 bg-[#F5F0EB] rounded w-1/4"></div>
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-20" data-testid="no-products">
                <div className="w-16 h-16 bg-[#F5F0EB] rounded-full flex items-center justify-center mx-auto mb-6">
                  <Search className="w-8 h-8 text-[#A8A29E]" />
                </div>
                <p className="text-[#1C1917] text-lg mb-2">
                  {searchQuery 
                    ? `No encontramos productos para "${searchQuery}"`
                    : 'No se encontraron productos'
                  }
                </p>
                <p className="text-[#A8A29E] text-sm mb-6">
                  {searchQuery 
                    ? 'Intenta con otras palabras o explora nuestras categorías'
                    : 'Intenta con otra categoría'
                  }
                </p>
                {(searchQuery || selectedCategory) && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-[#BA7B7C] hover:text-[#A36566] transition-colors"
                  >
                    Ver todos los productos
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-8" data-testid="products-grid">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filter Modal */}
      {showFilters && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-filter-modal">
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowFilters(false)}
          ></div>
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 max-h-[70vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-[#1C1917]">Filtrar</h3>
              <button 
                onClick={() => setShowFilters(false)}
                className="text-[#57534E]"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => handleCategoryChange('')}
                className={`w-full text-left py-3 px-4 rounded-xl transition-colors ${
                  !selectedCategory 
                    ? 'bg-[#BA7B7C] text-white' 
                    : 'bg-[#F5F0EB] text-[#57534E]'
                }`}
              >
                Todos los productos
              </button>
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => handleCategoryChange(category)}
                  className={`w-full text-left py-3 px-4 rounded-xl transition-colors ${
                    selectedCategory === category 
                      ? 'bg-[#BA7B7C] text-white' 
                      : 'bg-[#F5F0EB] text-[#57534E]'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shop;
